import React, { useRef, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';

const ConnectionLine = ({ startNode, endNode, label, isSelected, onClick }) => {
  const theme = useTheme();
  const pathRef = useRef(null);
  
  // Calculate and render bezier curve connections
  useEffect(() => {
    if (startNode && endNode && pathRef.current) {
      const path = bezierCurve(startNode, endNode);
      pathRef.current.setAttribute('d', path);
    }
  }, [startNode, endNode]);
  
  return (
    <g className="connection-group" onClick={onClick}>
      {/* Main connection path */}
      <path
        ref={pathRef}
        stroke={isSelected ? theme.palette.primary.main : theme.palette.grey[400]}
        strokeWidth={isSelected ? 3 : 2}
        fill="none"
        strokeDasharray={isSelected ? "none" : "none"}
        className="connection-path"
        markerEnd="url(#arrowhead)"
      />
      
      {/* Animated flow indicator */}
      <path
        ref={pathRef}
        stroke={theme.palette.primary.main}
        strokeWidth={1.5}
        fill="none"
        strokeDasharray="4"
        className="connection-flow"
        style={{
          strokeDashoffset: 0,
          animation: 'flowAnimation 30s linear infinite',
        }}
      />
      
      {/* Arrow at the end */}
      <marker
        id="arrowhead"
        markerWidth="10"
        markerHeight="7"
        refX="10"
        refY="3.5"
        orient="auto"
      >
        <polygon 
          points="0 0, 10 3.5, 0 7" 
          fill={theme.palette.primary.main} 
        />
      </marker>
      
      {/* Connection label */}
      {label && (
        <foreignObject
          x={(startNode.x + endNode.x) / 2 - 50}
          y={(startNode.y + endNode.y) / 2 - 15}
          width="100"
          height="30"
          style={{
            overflow: 'visible',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 500,
              border: `1px solid ${theme.palette.divider}`,
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

// Utility function to calculate bezier curve path
const bezierCurve = (start, end) => {
  // Control point calculation
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Make curve more pronounced for vertical connections
  const curveIntensity = Math.abs(dy) > Math.abs(dx) ? 0.5 : 0.2;
  const controlPointDistance = distance * curveIntensity;
  
  // Control points positioned along the path, but offset
  const cx1 = start.x + controlPointDistance;
  const cy1 = start.y;
  const cx2 = end.x - controlPointDistance;
  const cy2 = end.y;
  
  return `M ${start.x} ${start.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${end.x} ${end.y}`;
};

export default ConnectionLine; 